import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Link,
  IconButton,
  TextField,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import Modal from 'react-modal';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import ImageUploadModal from '../../../../utils/ImageUploadModal';

// Modal for a paid custom-purchase-link REQUEST (source:'customlink'). The board row
// is one link; this shows ALL its still-pending items as a basket. Every item is
// decided BUY or REJECT (nothing left pending); BUY items share ONE proof image,
// REJECT items are refunded per item. `item` = the take payload: {linkId, smileName,
// smileNameJp, phone, isAir, items:[{orderId, price, qty, deliveryFeeJp, itemUrl, imageUrl}]}.
const CustomLinkTaskModal = ({ isOpen, onClose, item, onComplete, style }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  // orderId → { action: 'buy' | 'reject', remarks, actualProductJpy, actualDeliveryJpy, actualQty }
  const [itemActions, setItemActions] = useState({});
  // Per-item price editing is gated so a stray click can't change a value: one item at a
  // time enters an edit mode with a working `draft`, committed only on ยืนยัน.
  const [editingItem, setEditingItem] = useState(null); // orderId or null
  const [draft, setDraft] = useState({});

  const linkId = item?.linkId ?? item?.id;
  const items = Array.isArray(item?.items) ? item.items : [];

  // Reset per case: default every item to BUY, clear proof.
  useEffect(() => {
    setSelectedFiles([]);
    const init = {};
    (Array.isArray(item?.items) ? item.items : []).forEach((it) => {
      // actuals prefilled with the ordered values → an unchanged field reconciles to a no-op.
      init[it.orderId] = {
        action: 'buy',
        remarks: '',
        actualProductJpy: it.price != null ? String(it.price) : '',
        actualDeliveryJpy: it.deliveryFeeJp != null ? String(it.deliveryFeeJp) : '',
        actualQty: it.qty != null ? String(it.qty) : '',
      };
    });
    setItemActions(init);
  }, [isOpen, linkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // A qty of exactly 0 means "couldn't buy any" → auto-flip to reject. Switching back to
  // buy while qty is 0 restores qty 1 (a buy needs qty ≥ 1). Empty (mid-typing) does nothing.
  const isZeroQty = (v) => v !== '' && v != null && Number(v) === 0;

  const setAction = (orderId, action) => {
    if (!action) return; // ToggleButtonGroup fires null when the active button is re-clicked
    setItemActions((prev) => {
      const cur = prev[orderId] || {};
      const next = { ...cur, action };
      if (action === 'buy' && isZeroQty(cur.actualQty)) next.actualQty = '1';
      return { ...prev, [orderId]: next };
    });
    if (editingItem === orderId) { setEditingItem(null); setDraft({}); } // toggling ends the edit
  };
  const setRemarks = (orderId, remarks) => {
    setItemActions((prev) => ({ ...prev, [orderId]: { ...prev[orderId], remarks } }));
  };

  // Edit a Buy row's actuals through a draft (confirm/cancel), so nothing changes by accident.
  const startEdit = (orderId) => {
    const a = itemActions[orderId] || {};
    setDraft({
      actualProductJpy: a.actualProductJpy ?? '',
      actualDeliveryJpy: a.actualDeliveryJpy ?? '',
      actualQty: a.actualQty ?? '',
    });
    setEditingItem(orderId);
  };
  const cancelEdit = () => { setEditingItem(null); setDraft({}); };
  const confirmEdit = (orderId) => {
    setItemActions((prev) => {
      const cur = prev[orderId] || {};
      const next = {
        ...cur,
        actualProductJpy: draft.actualProductJpy,
        actualDeliveryJpy: draft.actualDeliveryJpy,
        actualQty: draft.actualQty,
      };
      if (isZeroQty(draft.actualQty)) next.action = 'reject'; // bought none → refund the item
      return { ...prev, [orderId]: next };
    });
    setEditingItem(null);
    setDraft({});
  };

  const buyCount = items.filter((it) => itemActions[it.orderId]?.action === 'buy').length;
  const rejectCount = items.length - buyCount;

  const handleImageUploadConfirm = (files) => {
    setSelectedFiles(files);
    setShowImageUploadModal(false);
  };

  const copyAmount = (price) => {
    navigator.clipboard.writeText(`${price}円`)
      .then(() => toast.success('คัดลอกแล้ว'))
      .catch(() => {});
  };

  const handleConfirm = async () => {
    // An open price edit must be resolved (ยืนยัน/ยกเลิก) before the whole request submits.
    if (editingItem != null) {
      toast.warn('กรุณายืนยันหรือยกเลิกการแก้ไขราคาก่อน');
      return;
    }
    // A BUY needs the shared proof; an all-reject request needs none.
    if (buyCount > 0 && selectedFiles.length === 0) {
      toast.error('กรุณาอัพโหลดรูปภาพหลักฐานการซื้อ');
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      formData.append('purchaseId', linkId); // the LINK id
      formData.append('source', 'customlink');
      formData.append('status', 'confirmed');
      formData.append('items', JSON.stringify(
        items.map((it) => {
          const a = itemActions[it.orderId] || {};
          const row = { orderId: it.orderId, action: a.action || 'buy', remarks: a.remarks || null };
          // Send actuals only for BUY rows (reconcile). Blank = leave that field unchanged.
          if (row.action === 'buy') {
            if (a.actualProductJpy !== '') row.actualProductJpy = Number(a.actualProductJpy);
            if (a.actualDeliveryJpy !== '') row.actualDeliveryJpy = Number(a.actualDeliveryJpy);
            if (a.actualQty !== '') row.actualQty = Number(a.actualQty);
          }
          return row;
        })
      ));
      await onComplete(formData);
    } catch (error) {
      toast.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{ ...style, content: { ...style.content, width: '860px', maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' } }}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <Paper>
        <Box p={3}>
          {!item ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              <CircularProgress />
              <Typography variant="h6" color="text.secondary">กำลังโหลดข้อมูล...</Typography>
            </Box>
          ) : (
            <>
              {/* Header — one customer per request */}
              <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
                <Chip label="ลิงก์สั่งซื้อ (Custom)" color="warning" sx={{ fontWeight: 700, height: 40, fontSize: '1.05rem', '& .MuiChip-label': { px: 2 } }} />
                <Chip
                  label={item.isAir ? '✈️ ทางอากาศ (Air)' : '🚢 ทางเรือ (Sea)'}
                  sx={{
                    bgcolor: item.isAir ? '#dbeafe' : '#cffafe',
                    color: item.isAir ? '#1e3a8a' : '#164e63',
                    fontWeight: 700,
                    border: item.isAir ? '2px solid #3b82f6' : '2px solid #06b6d4',
                    height: 40,
                    fontSize: '1.05rem',
                    '& .MuiChip-label': { px: 2 },
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  คำสั่งซื้อ #{linkId} · {item.smileName || 'N/A'}{item.smileNameJp ? ` (${item.smileNameJp})` : ''}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                ติดต่อ: {item.phone || 'N/A'} · {items.length} รายการ
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {/* Item basket — each row decided buy / reject */}
              {items.map((it) => {
                const action = itemActions[it.orderId]?.action || 'buy';
                const isReject = action === 'reject';
                return (
                  <Box key={it.orderId} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 1.5, opacity: isReject ? 0.7 : 1 }}>
                    <Box display="flex" gap={2}>
                      <Box sx={{ width: 90, height: 90, flexShrink: 0, bgcolor: '#f5f5f5', borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {it.imageUrl
                          ? <img src={it.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          : <Typography variant="caption" color="text.secondary">ไม่มีรูป</Typography>}
                      </Box>
                      <Box flex={1} minWidth={0}>
                        {it.itemUrl
                          ? <Link href={it.itemUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all', fontSize: 14 }}>{it.itemUrl}</Link>
                          : <Typography variant="body2" color="text.secondary">ไม่มีลิงก์สินค้า</Typography>}
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Typography variant="h6" color="secondary.main" fontWeight="bold">¥{Number(it.price).toLocaleString()}</Typography>
                          <IconButton size="small" onClick={() => copyAmount(it.price)}><ContentCopyIcon fontSize="small" /></IconButton>
                          <Typography variant="body2" color="text.secondary">× {it.qty}</Typography>
                          <Typography variant="body2" color="text.secondary">· ค่าส่ง ¥{Number(it.deliveryFeeJp || 0).toLocaleString()}</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={action}
                          onChange={(e, v) => setAction(it.orderId, v)}
                        >
                          <ToggleButton value="buy" color="primary">ซื้อ</ToggleButton>
                          <ToggleButton value="reject" color="error">ปฏิเสธ</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Box>
                    {!isReject && (
                      editingItem === it.orderId ? (
                        <Box display="flex" gap={1.5} alignItems="center" mt={1.5} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">ซื้อจริง:</Typography>
                          <TextField size="small" label="ราคา/ชิ้น ¥" type="number" sx={{ width: 130 }}
                            value={draft.actualProductJpy ?? ''}
                            onChange={(e) => setDraft((d) => ({ ...d, actualProductJpy: e.target.value }))} />
                          <TextField size="small" label="จำนวน" type="number" sx={{ width: 100 }}
                            value={draft.actualQty ?? ''}
                            onChange={(e) => setDraft((d) => ({ ...d, actualQty: e.target.value }))} />
                          <TextField size="small" label="ค่าส่ง ¥" type="number" sx={{ width: 120 }}
                            value={draft.actualDeliveryJpy ?? ''}
                            onChange={(e) => setDraft((d) => ({ ...d, actualDeliveryJpy: e.target.value }))} />
                          <Button size="small" variant="contained" onClick={() => confirmEdit(it.orderId)}>ยืนยัน</Button>
                          <Button size="small" color="inherit" onClick={cancelEdit}>ยกเลิก</Button>
                        </Box>
                      ) : (
                        <Box display="flex" gap={1} alignItems="center" mt={1.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            ซื้อจริง: ¥{Number(itemActions[it.orderId]?.actualProductJpy || 0).toLocaleString()} × {itemActions[it.orderId]?.actualQty} · ค่าส่ง ¥{Number(itemActions[it.orderId]?.actualDeliveryJpy || 0).toLocaleString()}
                          </Typography>
                          <Button size="small" variant="outlined" onClick={() => startEdit(it.orderId)}>แก้ไขราคา</Button>
                        </Box>
                      )
                    )}
                    {isReject && (
                      <TextField
                        size="small" fullWidth sx={{ mt: 1.5 }}
                        label="เหตุผลการปฏิเสธ (ไม่บังคับ)"
                        value={itemActions[it.orderId]?.remarks || ''}
                        onChange={(e) => setRemarks(it.orderId, e.target.value)}
                      />
                    )}
                  </Box>
                );
              })}

              {/* Shared proof (only needed when at least one item is bought) */}
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  อัพโหลดหลักฐานการซื้อ (รูปเดียว ใช้กับสินค้าที่ซื้อทั้งหมด)
                </Typography>
                <Box sx={{ p: 2, border: '1px dashed #e0e0e0', borderRadius: 2, textAlign: 'center' }}>
                  <Button variant="outlined" onClick={() => setShowImageUploadModal(true)} disabled={buyCount === 0} sx={{ mb: 1 }}>
                    เลือกไฟล์
                  </Button>
                  {selectedFiles.length > 0 && (
                    <Typography variant="body2" color="text.secondary">เลือกไฟล์แล้ว {selectedFiles.length} ไฟล์</Typography>
                  )}
                  {buyCount === 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">ปฏิเสธทุกรายการ — ไม่ต้องมีหลักฐาน</Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  ซื้อ {buyCount} · ปฏิเสธ {rejectCount} รายการ
                </Typography>
                <Box display="flex" gap={2}>
                  <Button variant="contained" onClick={handleConfirm} disabled={isSubmitting || editingItem != null} sx={{ minWidth: 120 }}
                    title={editingItem != null ? 'ยืนยัน/ยกเลิกการแก้ไขราคาก่อน' : ''}>
                    {isSubmitting ? <CircularProgress size={22} /> : 'ยืนยันคำสั่งซื้อ'}
                  </Button>
                  <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>ปิด</Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <ImageUploadModal
        isOpen={showImageUploadModal}
        onRequestClose={() => setShowImageUploadModal(false)}
        onConfirm={handleImageUploadConfirm}
        title="อัพโหลดรูปภาพ"
        maxFiles={1}
        acceptedFileTypes={['image/*']}
        maxFileSize={2 * 1024 * 1024}
      />
    </Modal>
  );
};

export default CustomLinkTaskModal;
